import { CoreApiClient } from '../../core/client'
import { withQuery } from '../../core/query'
import type { PageResult, StandardSaasWorkbench } from '../../core/types'

/** 国学签到记录 */
export interface GuoxueDailySignRecord {
  id: number
  tenantId: number
  clientUserId: number
  username: string
  signDate: string
  cycleDay: number
  streakDays: number
  rewardPoints: number
  cardCode: string
  cardName: string
  source: string
  createdAt: string
  updatedAt: string
}

/** 国学签到统计 */
export interface GuoxueDailySignStats {
  tenantId: number
  today: string
  todaySignUsers: number
  todayRewardBeans: number
  streak7Users: number
  totalSignCount: number
}

/** 国学签到查询参数 */
export interface GuoxueDailySignRecordQuery {
  username?: string
  startDate?: string
  endDate?: string
  page?: number
  size?: number
}

/** 国学算命/测试工具记录（对应后端 AdminToolRecordView 序列化） */
export interface GuoxueToolRecord {
  id: number
  clientUserId: number
  displayName: string
  toolCode: string
  title: string
  request: Record<string, unknown>
  result: Record<string, unknown>
  status: string
  /** 计费状态：FREE / PAID / PENDING / EXPIRED（历史记录可能缺失/为空） */
  billingStatus?: string
  /** 计费金额（星币，后端 BigDecimal，序列化后可能为字符串或数字） */
  payAmountCoin?: string | number
  createdAt: string
}

/** 国学工具记录分页列表 */
export interface GuoxueToolRecordPage {
  records: GuoxueToolRecord[]
  total: number
  pageNum: number
  pageSize: number
}

/** 国学工具记录查询参数 */
export interface GuoxueToolRecordQuery {
  clientUserId?: number
  toolCode?: string
  pageNum?: number
  pageSize?: number
}

/** 国学客户端站内消息 */
export interface GuoxueClientMessage {
  id: number
  tenantId: number
  moduleCode: string
  productCode: string
  clientUserId: number
  username: string
  category: string
  title: string
  body: string
  summary: string
  tag: string
  icon: string
  level: string
  senderName: string
  route: string
  linkType: string
  bizType: string
  bizId: string
  sourceEventKey: string
  payloadJson: string
  readStatus: number
  readTime?: string | null
  deleted: number
  revoked: number
  expireTime?: string | null
  createTime: string
  updateTime: string
}

/** 国学消息查询参数 */
export interface GuoxueClientMessageQuery {
  clientUserId?: number
  username?: string
  category?: string
  readStatus?: number
  bizType?: string
  startDate?: string
  endDate?: string
  page?: number
  size?: number
}

/** 国学消息统计 */
export interface GuoxueClientMessageStats {
  totalCount: number
  unreadCount: number
  todayNew: number
  categoryDistribution: Record<string, number>
}

/** 发送国学消息请求 */
export interface SendGuoxueClientMessageRequest {
  clientUserId: number
  moduleCode?: string
  productCode?: string
  category: string
  title: string
  body?: string
  summary?: string
  tag?: string
  icon?: string
  level?: string
  senderName?: string
  route?: string
  linkType?: string
  bizType?: string
  bizId?: string
  sourceEventKey?: string
  payload?: Record<string, unknown>
}

/** 国学咨询导师 */
export interface GuoxueConsultExpert {
  id: number
  tenantId?: number
  name: string
  title: string
  avatarUrl: string
  bio: string
  description: string
  tags: string[]
  tagsJson?: string
  consultPrice: number
  onlineStatus: string
  status: string
  rankNo: number
  ratingPercent: number
  fanCount: number
  recentCallMinutes: number
  waitSeconds: number
  freeTrialMinutes: number
  sort: number
  createTime?: string
  updateTime?: string
  availableSlots?: string[]
  rating?: number
  online?: boolean
  followed?: boolean
  link?: string
}

/** 国学咨询服务包 */
export interface GuoxueConsultServicePackage {
  id: number
  tenantId?: number
  expertId: number
  expertName: string
  title: string
  subtitle: string
  lines: string[]
  linesJson?: string
  serviceType: string
  price: number
  starPrice: number
  durationMinutes: number
  status: string
  sort: number
}

/** 国学咨询评价 */
export interface GuoxueConsultReview {
  id: number
  tenantId?: number
  expertId: number
  expertName: string
  clientUserId?: number | null
  nicknameMask: string
  rating: number
  tags: string[]
  tagsJson?: string
  content: string
  reply: string
  callCount: number
  createTime: string
  status: string
}

/** 评价汇总 */
export interface GuoxueConsultReviewSummary {
  count: number
  averageRating: number
  hotTags: string[]
}

/** 导师详情 */
export interface GuoxueConsultExpertDetail extends GuoxueConsultExpert {
  expert: GuoxueConsultExpert
  isFollowed: boolean
  services: GuoxueConsultServicePackage[]
  reviewSummary: GuoxueConsultReviewSummary
  reviews: GuoxueConsultReview[]
}

/** 咨询订单 */
export interface GuoxueConsultOrder {
  orderId: number
  tenantId?: number
  clientUserId: number
  expertId: number
  expertName: string
  serviceId: number
  serviceTitle: string
  mode: string
  orderNo: string
  amount: number
  starAmount: number
  payStatus: string
  orderStatus: string
  signalingRoomId: string
  liveRoomId: string
  startTime?: string | null
  endTime?: string | null
  durationSeconds: number
  createTime: string
}

/** 导师排班 */
export interface GuoxueConsultSchedule {
  expertId: number
  expertName: string
  title: string
  onlineStatus: string
  status: string
  waitSeconds: number
  freeTrialMinutes: number
  availableSlots: string[]
}

/** 国学通用分页 */
export interface GuoxueLivePage<T> {
  records: T[]
  total: number
  page: number
  size: number
}

/** 直播看板数据 */
export interface GuoxueLiveDashboard {
  todayRoomCount: number
  liveRoomCount: number
  onlineCount: number
  micApplyingCount: number
  giftIncomeCoin: number
  micIncomeCoin: number
  abnormalRoomCount: number
}

/** 直播推流服务商配置 */
export interface GuoxueLiveProviderConfig {
  id: number
  tenantId: number
  moduleCode: string
  provider: string
  enabled: boolean
  regionId: string
  accessKeyId: string
  accessKeySecretMask: string
  liveDomain: string
  pushDomain: string
  appName: string
  whipBaseUrl: string
  whepBaseUrl: string
  roomServicePublicBaseUrl: string
  hostTokenExpireSeconds: number
  authKeyMask: string
  pushAuthExpireSeconds: number
  playAuthExpireSeconds: number
  rtsEnabled: boolean
  flvEnabled: boolean
  hlsEnabled: boolean
  recordingEnabled: boolean
  snapshotEnabled: boolean
  callbackUrl: string
  remark: string
  version: number
  updateTime: string
}

/** 保存直播服务商配置请求 */
export interface SaveGuoxueLiveProviderConfigRequest {
  enabled?: boolean
  regionId?: string
  accessKeyId?: string
  accessKeySecret?: string
  liveDomain?: string
  pushDomain?: string
  appName?: string
  whipBaseUrl?: string
  whepBaseUrl?: string
  roomServicePublicBaseUrl?: string
  hostTokenExpireSeconds?: number
  authKey?: string
  pushAuthExpireSeconds?: number
  playAuthExpireSeconds?: number
  rtsEnabled?: boolean
  flvEnabled?: boolean
  hlsEnabled?: boolean
  recordingEnabled?: boolean
  snapshotEnabled?: boolean
  callbackUrl?: string
  remark?: string
}

/** 直播服务商配置连通性测试结果 */
export interface GuoxueLiveProviderConfigTestResult {
  success: boolean
  message: string
  warnings: string[]
  config: GuoxueLiveProviderConfig
}

/** 直播房间 */
export interface GuoxueLiveRoom {
  id: number
  roomNo: string
  title: string
  anchorName: string
  status: string
  viewerCount: number
  likeCount: number
  onlineCount: number
  coverUrl: string
  link: string
}

/** 直播房间详情 */
export interface GuoxueLiveRoomDetail {
  room: GuoxueLiveRoom
  subtitle: string
  coverUrl: string
  anchorAvatar: string
  category: string
  notice: string
  allowMic: boolean
  allowGift: boolean
  allowChat: boolean
  scheduledStartTime?: string | null
  actualStartTime?: string | null
}

/** 保存直播房间请求 */
export interface SaveGuoxueLiveRoomRequest {
  roomNo?: string
  title: string
  subtitle?: string
  coverUrl?: string
  anchorId?: number | null
  anchorName: string
  anchorAvatar?: string
  category?: string
  status?: string
  streamName?: string
  appName?: string
  scheduledStartTime?: string | null
  allowMic?: boolean
  allowGift?: boolean
  allowChat?: boolean
  notice?: string
  sort?: number
}

/** 推流鉴权信息 */
export interface GuoxueLiveStreamAuth {
  id: number
  roomId: number
  streamName: string
  pushUrl: string
  rtsPlayUrl: string
  flvPlayUrl: string
  hlsPlayUrl: string
  expireTime: string
}

/** 媒体鉴权信息 */
export interface GuoxueLiveRoomMediaAuth {
  roomId: number
  streamName: string
  whipUrl: string
  whepUrl: string
  rtmpPushUrl: string
  rtsPlayUrl: string
  flvPlayUrl: string
  hlsPlayUrl: string
  pushExpireTime?: string | null
  playExpireTime?: string | null
}

/** 房间快照 */
export interface GuoxueLiveRoomSnapshot {
  roomId: number
  roomNo: string
  title: string
  status: string
  onlineCount: number
  micSeat: { status: string; occupant?: GuoxueLiveMicOrder | null; queueCount: number }
  media: GuoxueLiveRoomMediaAuth
}

/** 主播会话 */
export interface GuoxueLiveRoomSession {
  roomId: number
  hostToken: string
  hostTokenExpiresAt: string
  hostWebSocketUrl: string
  audienceWebSocketUrl: string
  media: GuoxueLiveRoomMediaAuth
  snapshot: GuoxueLiveRoomSnapshot
}

/** 直播异常记录 */
export interface GuoxueLiveException {
  id: number
  tenantId: number
  roomId?: number | null
  eventType: string
  severity: string
  errorCode: string
  message: string
  context: Record<string, unknown>
  resolved: boolean
  resolvedBy?: number | null
  resolvedAt?: string | null
  resolveNote?: string | null
  occurredAt: string
}

/** 连麦订单 */
export interface GuoxueLiveMicOrder {
  id: number
  orderNo: string
  roomId: number
  clientUserId: number
  anchorId?: number | null
  mode: string
  status: string
  sessionId: string
  priceCoinPerMinute: number
  freeMinutes: number
  startTime?: string | null
  connectedTime?: string | null
  hangupTime?: string | null
  durationSeconds: number
  chargedCoin: number
  walletLedgerId?: number | null
  cancelReason?: string | null
  finishReason?: string | null
}

/** 弹幕互动消息 */
export interface GuoxueLiveMessage {
  id: number
  roomId: number
  messageId: string
  senderType: string
  senderId?: number | null
  senderName: string
  messageType: string
  content: string
  payload: Record<string, unknown>
  auditStatus: string
  createTime: string
}

/** 礼物配置 */
export interface GuoxueLiveGiftConfig {
  giftCode: string
  giftName: string
  priceCoin: number
  enabled: boolean
}

/** 直播查询参数 */
export interface GuoxueLiveQuery {
  keyword?: string
  status?: string
  roomId?: number
  auditStatus?: string
  page?: number
  size?: number
}

/** 导师入驻申请 */
export interface GuoxueTeacherApplication {
  applicationId: number
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'SUSPENDED' | 'NOT_SUBMITTED'
  realName: string
  mobile: string
  teacherName: string
  title: string
  introduction: string
  specialties: string[]
  experienceYears: number
  credentialNo: string
  certificateUrls: string[]
  rejectReason: string
  submittedAt?: string | null
  reviewedAt?: string | null
  agreementVersion: string
  canStartLive: boolean
  clientUserId: number
  reviewedBy?: number | null
  updatedAt?: string | null
}

/** 导师申请审核日志 */
export interface GuoxueTeacherAuditLog {
  id: number
  action: string
  fromStatus?: string | null
  toStatus: string
  reason?: string | null
  operatorType: string
  operatorId?: number | null
  createTime: string
}

/** 导师申请详情 */
export interface GuoxueTeacherApplicationDetail {
  application: GuoxueTeacherApplication
  auditLogs: GuoxueTeacherAuditLog[]
}

/** 导师档案 */
export interface GuoxueTeacherProfile {
  id: number
  clientUserId: number
  applicationId: number
  teacherNo: string
  teacherName: string
  title: string
  avatarUrl: string
  introduction: string
  specialties: string[]
  status: 'ACTIVE' | 'SUSPENDED'
  allowLive: boolean
  allowConsult: boolean
  sharingRatio: number
  totalLiveMinutes: number
  totalMicMinutes: number
  fanCount: number
  rating: number
  createTime: string
  updateTime: string
}

/** 导师直播数据汇总 */
export interface GuoxueTeacherLiveSummary {
  profileId: number
  clientUserId: number
  roomCount: number
  liveRoomCount: number
  viewerCount: number
  onlineCount: number
  micCount: number
  micSeconds: number
  chargedCoin: number
  totalLiveMinutes: number
  totalMicMinutes: number
}

/** 导师分页列表 */
export interface GuoxueTeacherPage<T> {
  records: T[]
  total: number
  page: number
  size: number
}

/** 导师查询参数 */
export interface GuoxueTeacherQuery {
  status?: string
  keyword?: string
  clientUserId?: number | null
  page?: number
  size?: number
}

/** 国学用户钱包账户 */
export interface GuoxueWalletAccount {
  id: number
  memberId: number
  clientUserId?: number | null
  username: string
  displayName: string
  balanceCent: number
  frozenCent: number
  totalRechargeCent: number
  totalConsumeCent: number
  status: string
  updatedAt: string
}

/** 国学关于我们配置 */
export interface GuoxueAccountAbout {
  appName: string
  appVersion: string
  companyName: string
  customerServicePhone: string
  customerServiceUrl: string
  filingNumber: string
  icpNumber: string
  copyright: string
  description: string
  updatedAt: string
}

export type SaveGuoxueAccountAboutRequest = Omit<GuoxueAccountAbout, 'updatedAt'>

/** 国学前端注册用户 */
export interface GuoxueAccountUser {
  userId: number
  username: string
  displayName: string
  mobile: string
  enabled: boolean
  cancellationStatus: string
  createdAt: string
  updatedAt: string
}

export interface GuoxueAccountPage<T> {
  records: T[]
  total: number
  pageNum: number
  pageSize: number
}

/** 钱包流水 */
export interface GuoxueWalletTransaction {
  id: number
  memberId: number
  clientUserId?: number | null
  username: string
  type: string
  amountCent: number
  balanceAfterCent: number
  status: string
  remark: string
  createdAt: string
}

/** 充值订单 */
export interface GuoxueRechargeOrder {
  id: number
  orderNo: string
  memberId: number
  clientUserId?: number | null
  username: string
  amountCent: number
  coins: number
  payChannel: string
  payStatus: string
  paidAt?: string | null
  createdAt: string
}

/** 积分卡券奖励 */
export interface GuoxuePointRewardCoupon {
  id: number
  rewardCode: string
  title: string
  benefitCode: string
  benefitName: string
  costPoints: number
  issuedQuantity: number
  availableQuantity: number
  status: string
  sort: number
  updatedAt: string
}

/** 咨询师查询参数 */
export interface GuoxueConsultQuery {
  keyword?: string
  status?: string
  onlineStatus?: string
  expertId?: number
  clientUserId?: number
  page?: number
  size?: number
}

/** 咨询订单查询参数 */
export interface GuoxueConsultOrderQuery {
  expertId?: number
  clientUserId?: number
  status?: string
  page?: number
  size?: number
}

export interface SaveGuoxueConsultExpertRequest {
  id?: number | null
  name: string
  title: string
  avatarUrl?: string
  bio: string
  description: string
  tagsJson: string
  consultPrice: number
  onlineStatus: string
  status: string
  rankNo: number
  ratingPercent: number
  fanCount: number
  recentCallMinutes: number
  waitSeconds: number
  freeTrialMinutes: number
  sort: number
}

export interface SaveGuoxueConsultServiceRequest {
  id?: number | null
  expertId: number
  title: string
  subtitle: string
  linesJson: string
  serviceType: string
  price: number
  starPrice: number
  durationMinutes: number
  status: string
  sort: number
}

export interface UpdateGuoxueConsultReviewRequest {
  status: string
  reply: string
}

export interface UpdateGuoxueConsultScheduleRequest {
  onlineStatus: string
  status: string
  waitSeconds: number
  freeTrialMinutes: number
  availableSlots?: string[]
}

export interface CreateGuoxueConsultOrderRequest {
  expertId: number
  serviceId: number
  mode: string
  expectedMinutes?: number
}

export interface GuoxueConsultFollowResult {
  expertId: number
  followed: boolean
}

/** 国学 API 模块 */
export class GuoxueApiModule {
  private readonly client: CoreApiClient

  constructor(client: CoreApiClient) {
    this.client = client
  }

  getSaasGuoxueWorkbench() {
    return this.client.get<StandardSaasWorkbench>('/api/saas/guoxue/workbench')
  }

  getSaasGuoxueAccountAbout() {
    return this.client.get<GuoxueAccountAbout>('/api/saas/guoxue/account/about')
  }

  saveSaasGuoxueAccountAbout(body: SaveGuoxueAccountAboutRequest) {
    return this.client.put<GuoxueAccountAbout>('/api/saas/guoxue/account/about', body)
  }

  getSaasGuoxueAccountUsers(query: { keyword?: string; enabled?: boolean; pageNum?: number; pageSize?: number } = {}) {
    return this.client.get<GuoxueAccountPage<GuoxueAccountUser>>(withQuery('/api/saas/guoxue/account/users', query))
  }

  getSaasGuoxueWalletAccounts() {
    return this.client.get<GuoxueWalletAccount[]>('/api/saas/guoxue/wallet/accounts')
  }

  getSaasGuoxueWalletTransactions() {
    return this.client.get<GuoxueWalletTransaction[]>('/api/saas/guoxue/wallet/transactions')
  }

  getSaasGuoxueWalletRecharges() {
    return this.client.get<GuoxueRechargeOrder[]>('/api/saas/guoxue/wallet/recharges')
  }

  getSaasGuoxueCoupons() {
    return this.client.get<GuoxuePointRewardCoupon[]>('/api/saas/guoxue/shop/coupons')
  }

  getSaasGuoxueDailySignRecords(query: GuoxueDailySignRecordQuery = {}) {
    return this.client.get<PageResult<GuoxueDailySignRecord>>(withQuery('/api/saas/guoxue/daily-sign/records', query))
  }

  getSaasGuoxueDailySignStats() {
    return this.client.get<GuoxueDailySignStats>('/api/saas/guoxue/daily-sign/stats')
  }

  getSaasGuoxueToolRecords(query: GuoxueToolRecordQuery = {}) {
    return this.client.get<GuoxueToolRecordPage>(withQuery('/api/saas/guoxue/tools/records', query))
  }

  getSaasGuoxueMessages(query: GuoxueClientMessageQuery = {}) {
    return this.client.get<PageResult<GuoxueClientMessage>>(withQuery('/api/saas/guoxue/messages', query))
  }

  getSaasGuoxueMessageStats() {
    return this.client.get<GuoxueClientMessageStats>('/api/saas/guoxue/messages/stats')
  }

  sendSaasGuoxueMessage(body: SendGuoxueClientMessageRequest) {
    return this.client.post<GuoxueClientMessage>('/api/saas/guoxue/messages', body)
  }

  revokeSaasGuoxueMessage(messageId: number) {
    return this.client.put<GuoxueClientMessage>(`/api/saas/guoxue/messages/${messageId}/revoke`)
  }

  getSaasGuoxueConsultExperts(query: GuoxueConsultQuery = {}) {
    return this.client.get<PageResult<GuoxueConsultExpert>>(withQuery('/api/saas/guoxue/consult/experts', query))
  }

  saveSaasGuoxueConsultExpert(body: SaveGuoxueConsultExpertRequest) {
    if (body.id) {
      return this.client.put<GuoxueConsultExpert>(`/api/saas/guoxue/consult/experts/${body.id}`, body)
    }
    return this.client.post<GuoxueConsultExpert>('/api/saas/guoxue/consult/experts', body)
  }

  getSaasGuoxueConsultServices(query: GuoxueConsultQuery = {}) {
    return this.client.get<PageResult<GuoxueConsultServicePackage>>(withQuery('/api/saas/guoxue/consult/services', query))
  }

  saveSaasGuoxueConsultService(body: SaveGuoxueConsultServiceRequest) {
    if (body.id) {
      return this.client.put<GuoxueConsultServicePackage>(`/api/saas/guoxue/consult/services/${body.id}`, body)
    }
    return this.client.post<GuoxueConsultServicePackage>('/api/saas/guoxue/consult/services', body)
  }

  getSaasGuoxueConsultReviews(query: GuoxueConsultQuery = {}) {
    return this.client.get<PageResult<GuoxueConsultReview>>(withQuery('/api/saas/guoxue/consult/reviews', query))
  }

  updateSaasGuoxueConsultReview(reviewId: number, body: UpdateGuoxueConsultReviewRequest) {
    return this.client.put<GuoxueConsultReview>(`/api/saas/guoxue/consult/reviews/${reviewId}`, body)
  }

  getSaasGuoxueConsultOrders(query: GuoxueConsultOrderQuery = {}) {
    return this.client.get<PageResult<GuoxueConsultOrder>>(withQuery('/api/saas/guoxue/consult/orders', query))
  }

  getSaasGuoxueConsultSchedules(query: GuoxueConsultQuery = {}) {
    return this.client.get<PageResult<GuoxueConsultSchedule>>(withQuery('/api/saas/guoxue/consult/schedules', query))
  }

  updateSaasGuoxueConsultSchedule(expertId: number, body: UpdateGuoxueConsultScheduleRequest) {
    return this.client.put<GuoxueConsultSchedule>(`/api/saas/guoxue/consult/schedules/${expertId}`, body)
  }

  getSaasGuoxueLiveDashboard() {
    return this.client.get<GuoxueLiveDashboard>('/api/saas/guoxue/live/dashboard')
  }

  getSaasGuoxueLiveProviderConfig() {
    return this.client.get<GuoxueLiveProviderConfig>('/api/saas/guoxue/live/provider-config')
  }

  saveSaasGuoxueLiveProviderConfig(body: SaveGuoxueLiveProviderConfigRequest) {
    return this.client.post<GuoxueLiveProviderConfig>('/api/saas/guoxue/live/provider-config', body)
  }

  updateSaasGuoxueLiveProviderConfig(configId: number, body: SaveGuoxueLiveProviderConfigRequest) {
    return this.client.put<GuoxueLiveProviderConfig>(`/api/saas/guoxue/live/provider-config/${configId}`, body)
  }

  testSaasGuoxueLiveProviderConfig(body: SaveGuoxueLiveProviderConfigRequest = {}) {
    return this.client.post<GuoxueLiveProviderConfigTestResult>('/api/saas/guoxue/live/provider-config/test', body)
  }

  getSaasGuoxueLiveRooms(query: GuoxueLiveQuery = {}) {
    return this.client.get<GuoxueLivePage<GuoxueLiveRoom>>(withQuery('/api/saas/guoxue/live/rooms', query))
  }

  createSaasGuoxueLiveRoom(body: SaveGuoxueLiveRoomRequest) {
    return this.client.post<GuoxueLiveRoomDetail>('/api/saas/guoxue/live/rooms', body)
  }

  updateSaasGuoxueLiveRoom(roomId: number, body: SaveGuoxueLiveRoomRequest) {
    return this.client.put<GuoxueLiveRoomDetail>(`/api/saas/guoxue/live/rooms/${roomId}`, body)
  }

  startSaasGuoxueLiveRoom(roomId: number) {
    return this.client.post<GuoxueLiveRoomDetail>(`/api/saas/guoxue/live/rooms/${roomId}/start`)
  }

  pauseSaasGuoxueLiveRoom(roomId: number) {
    return this.client.post<GuoxueLiveRoomDetail>(`/api/saas/guoxue/live/rooms/${roomId}/pause`)
  }

  endSaasGuoxueLiveRoom(roomId: number) {
    return this.client.post<GuoxueLiveRoomDetail>(`/api/saas/guoxue/live/rooms/${roomId}/end`)
  }

  generateSaasGuoxueLivePushAuth(roomId: number) {
    return this.client.post<GuoxueLiveStreamAuth>(`/api/saas/guoxue/live/rooms/${roomId}/push-auth`)
  }

  createSaasGuoxueLiveHostSession(roomId: number) {
    return this.client.post<GuoxueLiveRoomSession>(`/api/saas/guoxue/live/rooms/${roomId}/host-session`)
  }

  getSaasGuoxueLiveMicApplications(query: GuoxueLiveQuery = {}) {
    return this.client.get<GuoxueLivePage<GuoxueLiveMicOrder>>(withQuery('/api/saas/guoxue/live/mic/applications', query))
  }

  acceptSaasGuoxueLiveMic(roomId: number, orderNo: string) {
    return this.client.post<GuoxueLiveMicOrder>(`/api/saas/guoxue/live/rooms/${roomId}/mic/${orderNo}/accept`)
  }

  rejectSaasGuoxueLiveMic(roomId: number, orderNo: string, reason = '后台拒绝') {
    return this.client.post<GuoxueLiveMicOrder>(`/api/saas/guoxue/live/rooms/${roomId}/mic/${orderNo}/reject`, { reason })
  }

  hangupSaasGuoxueLiveMic(roomId: number, orderNo: string, reason = '后台挂断') {
    return this.client.post<GuoxueLiveMicOrder>(`/api/saas/guoxue/live/rooms/${roomId}/mic/${orderNo}/hangup`, { reason })
  }

  getSaasGuoxueLiveRoomMessages(roomId: number, query: GuoxueLiveQuery = {}) {
    return this.client.get<GuoxueLivePage<GuoxueLiveMessage>>(withQuery(`/api/saas/guoxue/live/rooms/${roomId}/messages`, query))
  }

  sendSaasGuoxueLiveSystemMessage(roomId: number, content: string) {
    return this.client.post<GuoxueLiveMessage>(`/api/saas/guoxue/live/rooms/${roomId}/messages/system`, { content })
  }

  auditSaasGuoxueLiveMessage(messageId: string, auditStatus: string) {
    return this.client.post<GuoxueLiveMessage>(`/api/saas/guoxue/live/messages/${messageId}/audit`, { auditStatus })
  }

  getSaasGuoxueLiveGifts() {
    return this.client.get<GuoxueLiveGiftConfig[]>('/api/saas/guoxue/live/gifts')
  }

  getSaasGuoxueLiveExceptions(query: GuoxueLiveQuery & { resolved?: boolean | null } = {}) {
    return this.client.get<GuoxueLivePage<GuoxueLiveException>>(withQuery('/api/saas/guoxue/live/exceptions', query))
  }

  resolveSaasGuoxueLiveException(exceptionId: number, note = '已处理') {
    return this.client.post<GuoxueLiveException>(`/api/saas/guoxue/live/exceptions/${exceptionId}/resolve`, { note })
  }

  getSaasGuoxueTeacherApplications(query: GuoxueTeacherQuery = {}) {
    return this.client.get<GuoxueTeacherPage<GuoxueTeacherApplication>>(
      withQuery('/api/saas/guoxue/teachers/applications', query)
    )
  }

  getSaasGuoxueTeacherApplication(applicationId: number) {
    return this.client.get<GuoxueTeacherApplicationDetail>(
      `/api/saas/guoxue/teachers/applications/${applicationId}`
    )
  }

  approveSaasGuoxueTeacherApplication(applicationId: number) {
    return this.client.post<GuoxueTeacherApplicationDetail>(
      `/api/saas/guoxue/teachers/applications/${applicationId}/approve`
    )
  }

  rejectSaasGuoxueTeacherApplication(applicationId: number, reason: string) {
    return this.client.post<GuoxueTeacherApplicationDetail>(
      `/api/saas/guoxue/teachers/applications/${applicationId}/reject`,
      { reason }
    )
  }

  getSaasGuoxueTeacherProfiles(query: GuoxueTeacherQuery = {}) {
    return this.client.get<GuoxueTeacherPage<GuoxueTeacherProfile>>(
      withQuery('/api/saas/guoxue/teachers/profiles', query)
    )
  }

  suspendSaasGuoxueTeacherProfile(profileId: number, reason: string) {
    return this.client.post<GuoxueTeacherProfile>(`/api/saas/guoxue/teachers/profiles/${profileId}/suspend`, { reason })
  }

  resumeSaasGuoxueTeacherProfile(profileId: number) {
    return this.client.post<GuoxueTeacherProfile>(`/api/saas/guoxue/teachers/profiles/${profileId}/resume`)
  }

  getSaasGuoxueTeacherLiveSummary(profileId: number) {
    return this.client.get<GuoxueTeacherLiveSummary>(
      `/api/saas/guoxue/teachers/profiles/${profileId}/live-summary`
    )
  }

  getClientGuoxueExperts(query: GuoxueConsultQuery = {}) {
    return this.client.get<GuoxueConsultExpert[]>(withQuery('/api/client/guoxue/experts', query))
  }

  getClientGuoxueExpert(expertId: number) {
    return this.client.get<GuoxueConsultExpertDetail>(`/api/client/guoxue/experts/${expertId}`)
  }

  getClientGuoxueExpertServices(expertId: number) {
    return this.client.get<GuoxueConsultServicePackage[]>(`/api/client/guoxue/experts/${expertId}/services`)
  }

  getClientGuoxueExpertReviews(expertId: number, query: GuoxueConsultQuery = {}) {
    return this.client.get<GuoxueConsultReview[]>(withQuery(`/api/client/guoxue/experts/${expertId}/reviews`, query))
  }

  toggleClientGuoxueExpertFollow(expertId: number) {
    return this.client.post<GuoxueConsultFollowResult>(`/api/client/guoxue/experts/${expertId}/follow`)
  }

  createClientGuoxueConsultOrder(body: CreateGuoxueConsultOrderRequest) {
    return this.client.post<GuoxueConsultOrder>('/api/client/guoxue/consult/orders', body)
  }
}
